<template>
  <b-carousel
    v-if="images.length || !$store.getters['ui/isMobile']"
    :indicator-inside="false"
    :pause-info="false"
    :arrow="false"
    :indicator="images.length > 1"
  >
    <template v-if="images.length">
      <b-carousel-item v-for="(image, i) in images" :key="`image-${i}`">
        <picture class="has-preview" @click="preview(image.full_url)">
          <img :src="`${image.full_url}?size=400x400`" />
        </picture>
      </b-carousel-item>
    </template>
    <b-carousel-item v-else>
      <picture class="has-background-light">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
          <path
            class="has-text-light"
            fill="white"
            d="M512 144v288c0 26.5-21.5 48-48 48H48c-26.5
            0-48-21.5-48-48V144c0-26.5 21.5-48 48-48h88l12.3-32.9c7-18.7
            24.9-31.1 44.9-31.1h125.5c20 0 37.9 12.4 44.9 31.1L376 96h88c26.5 0
            48 21.5 48 48zM376 288c0-66.2-53.8-120-120-120s-120 53.8-120 120
            53.8 120 120 120 120-53.8 120-120zm-32 0c0 48.5-39.5 88-88
            88s-88-39.5-88-88 39.5-88 88-88 88 39.5 88 88zm-120 0c0-17.6 14.4-32
            32-32 8.8 0 16-7.2 16-16s-7.2-16-16-16c-35.3 0-64 28.7-64 64 0 8.8
            7.2 16 16 16s16-7.2 16-16z"
          />
        </svg>
      </picture>
    </b-carousel-item>

    <template v-if="images.length > 1" #indicators="{ i }">
      <picture>
        <img :src="`${images[i].full_url}?size=400x400`" :title="i" />
      </picture>
    </template>
  </b-carousel>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { DataModules } from "@/store/modules/data/modules";
import type { PropType } from "vue";
import type { IImage } from "@/store/types";

export default defineComponent({
  name: "UImageCarousel",
  props: {
    images: { type: Array as PropType<IImage[]>, default: () => [] }
  },
  methods: {
    preview(full_url: IImage["full_url"]) {
      if (!full_url) return;
      this.$store.dispatch(`data/${DataModules.IMAGES}/openImagePreviewModal`, {
        props: {
          imagePath: `${full_url}?size=original`
        }
      });
    }
  }
});
</script>

<style lang="scss" scoped>
::v-deep.carousel {
  // 1:1 Aspect ratio
  .carousel-item,
  .carousel-indicator {
    picture {
      display: block;
      position: relative;
      width: 100%;
      padding-top: 100%;
      border-radius: $radius-large;
      overflow: hidden;
      img,
      svg {
        display: block;
        position: absolute;
      }
      img {
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      svg {
        top: 35%;
        left: 35%;
        width: 30%;
        height: 30%;
      }
    }
  }

  .carousel-item picture.has-preview {
    &::before {
      content: "";
      display: block;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;
      background-color: transparent;
      transition: background-color ease-out 0.2s;
    }

    &::after {
      content: "\f31e";
      font-family: $family-icon;
      font-size: 1.5rem;
      font-weight: 900;
      line-height: 1;
      font-style: normal;
      font-variant: normal;
      text-rendering: auto;
      -webkit-font-smoothing: antialiased;
      position: absolute;
      top: 50%;
      left: 50%;
      opacity: 0;
      z-index: 2;
      color: $white;
      transform: translate(-50%, -50%);
      transition: ease-in 0.2s;
      transition-property: transform, opacity;
    }

    &:hover {
      cursor: pointer;
      &::before {
        background-color: rgba($black, 0.33);
      }
      &::after {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1.5);
      }
    }
  }

  .carousel-indicator {
    justify-content: flex-start;
    flex-wrap: wrap;
    width: auto;
    padding: 0.5rem 0 0;
    margin: -0.25rem;
    .indicator-item {
      margin: 0.25rem;
      width: calc(20% - 0.5rem);
      &:not(.is-active) {
        opacity: 0.75;
      }
    }
  }
}
</style>
